import Indicator from '@/components/UI/Indicator';
import { Panel, Tab, Tabs } from '@/components/UI/Tabs';
import type { DraftFormData } from '@/model/Draft.model';
import type { FC } from 'react';
import type React from 'react';
import { useEffect, useMemo, useState } from 'react';
import { GroupAndTreatmentConfiguration } from './GroupAndTreatmentConfiguration';
import { GroupCreation } from './GroupCreation';
import { TreatmentCreation } from './TreatmentCreation';
import { createSelector } from '@reduxjs/toolkit';
import { type State } from '@/model/State.model';
import { useSelector } from 'react-redux';
import { useStudyCreateGroups, useStudyCreateStep } from '@/utils/studyCreation/useStudyCreate.tsx';
import type { DisplayedGroupMetadata, MetadataGlossaryV1 } from '@/model/Metadata.model';
import {
  type GroupValidationIssue,
  type TreatmentValidationIssue,
  validateGroups,
  validateTreatments,
} from './GroupsAndTreatment.utils';
import { useLegacyApiHook } from '@/support/Hooks/api/useLegacyApiHook';
import { useApiHook } from '@/support/Hooks/api/useApiHook';
import type { ID } from '@/model/Common.model';

interface GroupsAndTreatmentsCreationProps {
  state: { study: DraftFormData };
  dispatch: React.Dispatch<unknown>;
}

const selectDiseaseInduction = createSelector(
  (state: State) => state.team?.features?.disease_induction ?? false,
  (disease_induction) => ({ disease_induction })
);

export const GroupsAndTreatmentsCreation: FC<GroupsAndTreatmentsCreationProps> = ({ state: { study }, dispatch }) => {
  const { disease_induction } = useSelector(selectDiseaseInduction);
  const { setStepReady } = useStudyCreateStep();
  const { groups, treatments } = useStudyCreateGroups();
  const [groupIssues, setGroupIssues] = useState<Map<string, GroupValidationIssue>>(new Map());

  // Fetch treatment metadata
  const { response: treatmentMetadataResponse, loading: treatmentMetaLoading } = useApiHook({
    endpoint: 'GET /api/v1/metadata-glossary',
    query: { filter_type: 'treatment_meta' },
    invokeOnInit: true,
    options: { onError: { slug: 'treatment-meta-load' } },
  });

  // Fetch disease metadata only when disease_induction feature is enabled
  const { response: diseaseMetadataResponse, loading: diseaseMetaLoading } = useApiHook({
    endpoint: 'GET /api/v1/metadata-glossary',
    query: { filter_type: 'disease_meta' },
    invokeOnInit: disease_induction,
    options: { onError: { slug: 'disease-meta-load' } },
  });

  // Fetch group metadata settings (for required fields and options validation)
  const { response: studyGroupsResponse, loading: studyGroupsLoading } = useLegacyApiHook({
    method: 'GET',
    apiRoute: 'team.studies.groups',
    options: { onError: { toast: true, slug: 'groups-load' } },
  });

  // Memoize API response data extraction to prevent unnecessary recalculations
  const treatmentMetadata = useMemo<Array<MetadataGlossaryV1> | undefined>(
    () => (treatmentMetadataResponse?.type === 'success' ? treatmentMetadataResponse.body : undefined),
    [treatmentMetadataResponse]
  );

  const diseaseMetadata = useMemo<Array<MetadataGlossaryV1> | undefined>(
    () => (diseaseMetadataResponse?.type === 'success' ? diseaseMetadataResponse.body : undefined),
    [diseaseMetadataResponse]
  );

  const savedGroupData = useMemo(
    () =>
      studyGroupsResponse?.type === 'success'
        ? (studyGroupsResponse.body as { data: Array<DisplayedGroupMetadata> })?.data
        : undefined,
    [studyGroupsResponse]
  );

  // Compute required metadata fields from group settings
  const requiredGroupMetadata = useMemo<Array<ID> | undefined>(() => {
    return savedGroupData?.reduce<Array<ID>>((acc, setting) => {
      if (setting.required) {
        acc.push(setting?.schema?.id);
      }
      return acc;
    }, []);
  }, [savedGroupData]);

  // Partition treatments by classification in a single pass
  const { regularTreatments, diseaseTreatments } = useMemo(() => {
    const treatmentList = treatments ?? [];
    return treatmentList.reduce<{
      regularTreatments: typeof treatmentList;
      diseaseTreatments: typeof treatmentList;
    }>(
      (acc, treatment) => {
        if (treatment.treatment_classification === 'disease_induction') {
          acc.diseaseTreatments.push(treatment);
        } else {
          acc.regularTreatments.push(treatment);
        }
        return acc;
      },
      { regularTreatments: [], diseaseTreatments: [] }
    );
  }, [treatments]);

  const treatmentIssues = useMemo<Map<string, TreatmentValidationIssue>>(() => {
    if (treatmentMetaLoading) {
      return new Map();
    }
    return validateTreatments(regularTreatments, treatmentMetadata);
  }, [regularTreatments, treatmentMetadata, treatmentMetaLoading]);

  const diseaseIssues = useMemo<Map<string, TreatmentValidationIssue>>(() => {
    if (diseaseMetaLoading || !disease_induction) {
      return new Map();
    }
    return validateTreatments(diseaseTreatments, diseaseMetadata);
  }, [diseaseTreatments, diseaseMetadata, diseaseMetaLoading, disease_induction]);

  // Compute group issues (async because validateGroups supports lookup field validation)
  useEffect(() => {
    let cancelled = false;

    const computeGroupIssues = async () => {
      if (studyGroupsLoading) {
        return;
      }
      const issues = await validateGroups(groups ?? [], requiredGroupMetadata ?? [], savedGroupData);
      if (!cancelled) {
        setGroupIssues(issues);
      }
    };
    computeGroupIssues();

    return () => {
      cancelled = true;
    };
  }, [groups, requiredGroupMetadata, savedGroupData, studyGroupsLoading]);

  useEffect(() => {
    const hasIssues = groupIssues.size > 0 || treatmentIssues.size > 0 || diseaseIssues.size > 0;
    setStepReady(!hasIssues);
  }, [groupIssues, treatmentIssues, diseaseIssues, setStepReady]);

  return (
    <div data-testid="groups-and-treatments__container">
      <Tabs>
        <div className="bb b--moon-gray">
          <Tab className="f5 f4-l">
            {groupIssues.size > 0 && <Indicator smallIndicator indicatorClassName="ml2 mr1 p v-mid" />}
            Groups
          </Tab>
          {disease_induction && (
            <Tab className="f5 f4-l">
              {diseaseIssues.size > 0 && <Indicator smallIndicator indicatorClassName="ml2 mr1 p v-mid" />}
              Diseases
            </Tab>
          )}
          <Tab className="f5 f4-l" testId="study-create-treatments">
            {treatmentIssues.size > 0 && <Indicator smallIndicator indicatorClassName="ml2 mr1 p v-mid" />}
            Treatments
          </Tab>
          <Tab className="f5 f4-l" testId="study-create-configuration">
            Configuration
          </Tab>
        </div>
        <Panel>
          <GroupCreation study={study} dispatch={dispatch} groupIssues={groupIssues} savedGroupData={savedGroupData} />
        </Panel>
        {disease_induction && (
          <Panel>
            <TreatmentCreation
              study={study}
              classificationType="disease_induction"
              dispatch={dispatch}
              treatmentIssues={diseaseIssues}
            />
          </Panel>
        )}
        <Panel>
          <TreatmentCreation
            study={study}
            classificationType="treatment"
            dispatch={dispatch}
            treatmentIssues={treatmentIssues}
          />
        </Panel>
        <Panel>
          <GroupAndTreatmentConfiguration study={study} dispatch={dispatch} />
        </Panel>
      </Tabs>
    </div>
  );
};
